import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module-level cache (survives StrictMode remounts + back/forward nav)
const _verifyCache = new Map(); // token -> Promise<{status,email,message}>

/**
 * /verify?token=XYZ
 * Confirms an email, adds the contact to the Brevo list and celebrates.
 */
export default function VerifyEmailPage() {
    const [sp] = useSearchParams();
    const token = sp.get("token") || "";
    const [state, setState] = useState({
        status: "loading", // loading | verified | already | error
        email: "",
        message: "",
    });

    useEffect(() => {
        if (!token) {
            setState({
                status: "error",
                email: "",
                message: "No verification token provided.",
            });
            return;
        }

        // Module-level cache dedupes StrictMode double-invoke, back/forward nav,
        // and any other mount-cycle that fires the same verify twice.
        let promise = _verifyCache.get(token);
        if (!promise) {
            promise = axios
                .get(`${API}/signup/verify`, { params: { token } })
                .then((r) => ({
                    status:
                        r.data?.status === "already_verified"
                            ? "already"
                            : r.data?.status === "verified"
                              ? "verified"
                              : "error",
                    email: r.data?.email || "",
                    message: r.data?.message || "",
                }))
                .catch((err) => ({
                    status: "error",
                    email: "",
                    message:
                        err?.response?.data?.detail ||
                        "We couldn't verify this link. It may have already been used or expired.",
                }));
            _verifyCache.set(token, promise);
        }

        let active = true;
        promise.then((result) => {
            if (active) setState(result);
        });
        return () => {
            active = false;
        };
    }, [token]);

    return (
        <div
            className="min-h-screen bg-gradient-to-br from-[#FAF9F6] via-[#F0EEE3] to-[#E6F3F1] relative overflow-hidden flex items-center justify-center px-4 py-16"
            data-testid="verify-page"
        >
            <div className="absolute -top-24 -left-24 w-[520px] h-[520px] rounded-full bg-[#FF5E3A]/15 blur-3xl pointer-events-none" />
            <div className="absolute top-1/3 -right-32 w-[560px] h-[560px] rounded-full bg-[#FACC15]/20 blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-1/3 w-[480px] h-[480px] rounded-full bg-[#567f9b]/15 blur-3xl pointer-events-none" />

            <div className="relative z-10 max-w-xl w-full">
                {state.status === "loading" && (
                    <div className="rounded-3xl bg-white/95 backdrop-blur-xl border border-slate-200 p-10 text-center shadow-xl">
                        <Loader2 className="w-10 h-10 text-[#0D9488] animate-spin mx-auto" />
                        <p className="mt-5 font-display font-bold text-xl text-slate-900">
                            Confirming your spot…
                        </p>
                    </div>
                )}

                {(state.status === "verified" ||
                    state.status === "already") && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96, y: 12 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        data-testid="verify-success"
                        className="relative rounded-3xl overflow-hidden bg-slate-900 text-white p-10 sm:p-14 text-center shadow-[0_30px_80px_-20px_rgba(86,127,155,0.5)]"
                    >
                        <div className="absolute inset-0 opacity-60 bg-[radial-gradient(ellipse_at_top_right,_rgba(13,148,136,0.5),_transparent_55%),radial-gradient(ellipse_at_bottom_left,_rgba(250,204,21,0.35),_transparent_55%),radial-gradient(ellipse_at_bottom_right,_rgba(236,72,153,0.35),_transparent_55%)]" />
                        <div className="relative z-10">
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{
                                    type: "spring",
                                    delay: 0.15,
                                    bounce: 0.5,
                                }}
                                className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#0D9488] mb-6 shadow-[0_0_0_10px_rgba(13,148,136,0.25)]"
                            >
                                <CheckCircle2 className="w-8 h-8" />
                            </motion.div>
                            <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tighter leading-none">
                                You're in.
                            </h1>
                            <p className="mt-3 text-2xl sm:text-3xl font-display font-bold text-[#FACC15]">
                                This is going to be big.
                            </p>
                            <p className="mt-6 text-slate-300 max-w-sm mx-auto">
                                {state.status === "already"
                                    ? "You were already confirmed — great to have you!"
                                    : "Your email is confirmed and your spot is locked in."}{" "}
                                See you{" "}
                                <span className="text-white font-semibold">
                                    Saturday, 18 July
                                </span>
                                .
                            </p>
                            <div className="mt-8 inline-flex flex-wrap gap-3 justify-center">
                                <Link
                                    to="/"
                                    className="px-6 py-3 rounded-full bg-[#FACC15] text-slate-900 text-sm font-bold hover:bg-yellow-300 transition-colors"
                                >
                                    Back to home →
                                </Link>
                            </div>
                        </div>
                    </motion.div>
                )}

                {state.status === "error" && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        data-testid="verify-error"
                        className="rounded-3xl bg-white/95 backdrop-blur-xl border-2 border-[#FF5E3A]/40 p-10 sm:p-12 text-center shadow-xl"
                    >
                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#FF5E3A]/15 text-[#FF5E3A] mb-5">
                            <AlertCircle className="w-7 h-7" />
                        </div>
                        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-slate-900 leading-tight">
                            Link not valid
                        </h1>
                        <p className="mt-3 text-slate-600">{state.message}</p>
                        <Link
                            to="/"
                            className="mt-6 inline-flex items-center justify-center gap-2 bg-[#0D9488] hover:bg-[#0F766E] text-white font-bold rounded-full px-6 py-3 text-sm transition-colors"
                        >
                            Sign up again
                        </Link>
                    </motion.div>
                )}
            </div>
        </div>
    );
}
